// ─────────────────────────────────────────────────────────────
// invoicePrint.config.js — Report 4: single sales invoice
// Shape: document (portrait)
// Data:  GET /api/tape/sales/:id  (header + items[])
//
// COGS never appears here. tape_sales_items carries unit_cost
// and cogs_amount, but those are internal — a customer copy
// must not disclose what the goods cost us.
// ─────────────────────────────────────────────────────────────

import { TERMS_INVOICE, SIGNATURES, FORMATTERS } from '../../print/printConfig';

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

export const invoicePrintConfig = (inv) => {
  const items = inv.items || [];

  const rows = items.map((it, i) => ({
    sr: i + 1,
    product: it.product_name,
    description: [
      it.tape_type_name,
      it.size_inches ? `${it.size_inches}"` : null,
      it.is_printed
        ? `Printed${it.print_design ? ` · ${it.print_design}` : ''}`
        : 'Plain',
    ]
      .filter(Boolean)
      .join(' · '),
    quantity: it.quantity,
    rate: it.rate,
    amount: it.amount,
  }));

  const totalRolls = items.reduce((s, it) => s + num(it.quantity), 0);
  const grandTotal = num(inv.grand_total);

  const creditTerms =
    inv.customer_payment_type === 'credit'
      ? `Credit — ${inv.customer_payment_terms_days || 0} days`
      : 'Cash / On delivery';

  return {
    kind: 'document',
    orientation: 'p',
    title: 'Sales Invoice',

    boxes: [
      {
        heading: 'Bill To',
        rows: [
          { label: 'Customer', value: inv.customer_name },
          { label: 'Address', value: inv.customer_address },
          { label: 'Contact', value: inv.customer_contact_person },
          { label: 'Phone', value: inv.customer_phone },
          {
            label: 'NTN / STRN',
            value: `${inv.customer_ntn || '—'}  /  ${inv.customer_strn || '—'}`,
          },
        ],
      },
      {
        heading: 'Invoice Details',
        rows: [
          {
            label: 'Invoice #',
            value: inv.invoice_number,
            label2: 'Date',
            value2: FORMATTERS.date(inv.invoice_date),
          },
          {
            label: 'Lines',
            value: items.length,
            label2: 'Total Rolls',
            value2: FORMATTERS.int(totalRolls),
          },
          { label: 'Payment', value: creditTerms },
          { label: 'Notes', value: inv.notes },
        ],
      },
    ],

    columns: [
      { key: 'sr', label: '#', align: 'center', fmt: 'int', width: 10 },
      { key: 'product', label: 'Product', align: 'left', fmt: 'text', width: 40 },
      { key: 'description', label: 'Description', align: 'left', fmt: 'text' },
      { key: 'quantity', label: 'Rolls', align: 'right', fmt: 'int', width: 18 },
      { key: 'rate', label: 'Rate', align: 'right', fmt: 'money2', width: 24 },
      { key: 'amount', label: 'Amount', align: 'right', fmt: 'money', width: 28, bold: true },
    ],

    rows,

    footRow: [
      '',
      'TOTAL',
      '',
      FORMATTERS.int(totalRolls),
      '',
      FORMATTERS.money(grandTotal),
    ],

    totals: [
      { label: 'Total Rolls', value: totalRolls, fmt: 'int' },
      { label: 'Grand Total', value: grandTotal, fmt: 'money', emphasis: true, tone: 'net' },
    ],

    terms: TERMS_INVOICE,
    signatures: SIGNATURES.invoice,
  };
};

export default invoicePrintConfig;